import { GameModel } from "@nolimitcity/slot-game/bin/model/GameModel";
import { APIFeatureBetEventData } from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import { APIBetType } from "@nolimitcity/slot-launcher/bin/interfaces/APIBetType";
import { APIEvent } from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import { ApiPlugin } from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";

import { EventsManager, GameEvents } from "../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../abstracts/flow/AbstractGameMode";
import { ColorSet, log } from "../../abstracts/utils/AbstractConsole";
import { getRandomBetween } from "../../abstracts/utils/JSUtils";
import { Apocalypse } from "../Apocalypse";
import { falseArray, reelsConfig } from "../config/ApocalypseConfig";
import {
    ApocalypseHighSymbolNames,
    ApocalypseSpecialSymbolNames,
    ApocalypseSymbolNames,
} from "../types/ApocalypseEnums";
import {
    ApocalypseCoordType,
    ApocalypseFeaturePropertyObject,
    ApocalypseGameData,
    ApocalypseGameFeatureObj,
    ApocalypseInitData,
    ApocalypseM5Obj,
    ApocalypseM5ServerType,
    ApocalypseServerFeatureType,
} from "../types/ApocalypseInterfaces";

export class ApocalypseModel extends GameModel<ApocalypseInitData, ApocalypseGameData> {
    public isRestore: boolean = false;
    public buyFeatureName: string = ``;
    public xNudgeMap: boolean[] = [];
    public wildMap: boolean[] = [];
    public hasWildsToNudge: boolean = false;
    public finalWinningPosition: boolean[][] = [];
    public isLuckyDraw: boolean = false;
    public isXbet: boolean = false;
    public isSXNBet: boolean = false;
    public featuresMap: ApocalypseGameFeatureObj[];
    public m5RevealMap: ApocalypseM5Obj[] = [];
    public persistenceMap: boolean[][];
    public secondStageTeaserRounds: number[] = [];
    public currentSecondStageRound: number = 0;
    public lostRounds: number = 0;

    constructor(api: ApiPlugin, triggerEvents: boolean = true) {
        super(api, triggerEvents);

        api.events.on(`featureName`, (data: string) => {
            this.buyFeatureName = data;
        });

        const handleBet = (data: APIFeatureBetEventData) => {
            this.isLuckyDraw = data.playerInteraction?.featureName === `LUCKY_DRAW`;

            if (
                data.type == APIBetType.FEATURE_BET &&
                data.playerInteraction.featureName !== `BOOSTED_BET`
            ) {
                EventsManager.trigger(GameEvents.ON_FREE_BETS_START);
            }

            if (data.featureName) {
                this.isXbet = data.featureName === `BOOSTED_BET`;
                this.isSXNBet = data.featureName === `XNUDGE_BET`;

                this.buyFeatureName = data.featureName;
            } else {
                this.isXbet = false;
                this.isSXNBet = false;
            }
        };

        Apocalypse.apiPlugin.events.on(APIEvent.BET, (data: APIFeatureBetEventData) =>
            handleBet(data)
        );

        Apocalypse.apiPlugin.events.on(APIEvent.ACTION_SPINS_BET, (data: APIFeatureBetEventData) =>
            handleBet(data)
        );
    }

    private _currentSymbols: string[][] = [];

    public set currentSymbols(symbols: string[][]) {
        symbols.forEach((reel, reelIndex) => {
            this._currentSymbols[reelIndex] = reel;
        });
    }

    public get currentSymbols(): string[][] {
        return this._currentSymbols;
    }

    public get isFirstStage(): boolean {
        if (this.data.secondStage) {
            return false;
        }

        return this.data.firstStage && this.data.freespinsLeft !== 0;
    }

    private setInitialSymbols(): void {
        const initialSymbols: string[][] = [];

        let clonedReels = [...this.initData.reels];

        // const rowsNumber: number =
        //     this.initData.mode === AbstractGameMode.NORMAL
        //         ? reelsConfig.normalRowsNumber
        //         : reelsConfig.fsRowsNumber;

        if (this.initData.mode === AbstractGameMode.NORMAL) {
            for (let index: number = 0; index < reelsConfig.reelsNumber; index++) {
                const reel: string[] = clonedReels[index].filter(
                    (symbolName, index) => symbolName && index > 0 && index < 5
                );

                initialSymbols[index] = reel;
            }

            this.currentSymbols = initialSymbols;
        } else {
            if (this.initData.isRestoreState) {
                clonedReels = [...this.initData.evaluatedArea];
            }
            this.currentSymbols = clonedReels;
        }
    }

    private resetPersistanceMap(data: ApocalypseInitData): void {
        this.persistenceMap = JSON.parse(JSON.stringify(falseArray));
        data.persistentRows.forEach((reel, reelIndex) => {
            if (reel[0]) {
                this.persistenceMap[reelIndex][0] = true;
            }
            if (reel[1]) {
                this.persistenceMap[reelIndex][reelsConfig.fsRowsNumber - 1] = true;
            }
        });
    }

    protected onInit(data: ApocalypseInitData): void {
        log(`init data`, ColorSet.DARK_RED, data);

        this.featuresMap = [];
        this.m5RevealMap = [];

        this.resetPersistanceMap(data);

        super.onInit(data);

        this.isRestore = data.isRestoreState;

        this.setInitialSymbols();

        if (data.mode === AbstractGameMode.NORMAL && data.nextMode === AbstractGameMode.NORMAL) {
            this.secondStageTeaserRounds = [];
            this.currentSecondStageRound = 0;
        } else {
            this.setTeaserRounds(data);
        }
    }

    public setTeaserRounds(data: ApocalypseInitData): void {
        if (data.mode !== AbstractGameMode.NORMAL && !this.isFirstStage) {
            if (this.secondStageTeaserRounds.length === 0) {
                let maxRounds: number = data.secondStageFsLeft > 5 ? 5 : data.secondStageFsLeft;
                this.secondStageTeaserRounds.push(getRandomBetween(1, maxRounds, true));

                if (
                    data.nextMode === AbstractGameMode.SUPER_FREESPIN ||
                    data.nextMode === AbstractGameMode.FREESPIN_BETTER
                ) {
                    this.secondStageTeaserRounds.push(getRandomBetween(8, 10, true));
                }
            }

            this.currentSecondStageRound++;
        }
    }

    protected onGame(data: ApocalypseGameData): void {
        log(`new data`, ColorSet.RED, data);
        // console.log(`sandru new data`, data);

        this.isRestore = data.isRestoreState !== undefined ? data.isRestoreState : false;

        this.xNudgeMap = [];
        this.wildMap = [];
        this.hasWildsToNudge = false;

        const clonedReels =
            data.mode !== AbstractGameMode.NORMAL
                ? JSON.parse(JSON.stringify(data.evaluatedArea))
                : JSON.parse(JSON.stringify(data.reels));

        if (data.mode === AbstractGameMode.NORMAL) {
            for (let index: number = 0; index < clonedReels.length; index++) {
                const reel: string[] = clonedReels[index].filter(
                    (symbolName: string, index: number) =>
                        symbolName && index > 0 && index < clonedReels.length - 1
                );

                clonedReels[index] = reel;
            }
        }

        let wildRows: number[] = [];

        clonedReels.forEach((reel: string[], reelIndex: number) => {
            this.wildMap[reelIndex] = false;

            reel.forEach((symbol, symbolIndex) => {
                if (symbol === `WILD`) {
                    this.wildMap[reelIndex] = true;
                    wildRows.push(symbolIndex);
                }
            });

            this.xNudgeMap[reelIndex] = reel.includes(ApocalypseSpecialSymbolNames.XNUDGE);
            // !this.wildMap[reelIndex] && data.nudgeMultiplier[reelIndex] > 1;
        });

        if (wildRows.length === 2 && wildRows[0] === wildRows[1]) {
            this.hasWildsToNudge = true;
        }

        //handle features map
        let secondStageFeatures: ApocalypseServerFeatureType[] = Array.isArray(
            data.featuresMapForStageTwo
        )
            ? data.featuresMapForStageTwo
            : [];

        let m5Features: ApocalypseM5ServerType[] = Array.isArray(data.featuresMapFSM5)
            ? data.featuresMapFSM5
            : [];

        ////// start handle fs features
        this.featuresMap = [];
        this.m5RevealMap = [];
        if (data.secondStage) {
            if (secondStageFeatures.length > 0) {
                this.featuresMap = data.featuresMapForStageTwo.map(
                    (feature: ApocalypseServerFeatureType): ApocalypseGameFeatureObj => {
                        const symbolName = Object.keys(feature[1])[0];
                        const targetArr = feature[1][
                            symbolName as keyof ApocalypseFeaturePropertyObject
                        ] as ApocalypseCoordType[];

                        return {
                            name: symbolName as ApocalypseHighSymbolNames,
                            symbolCoord: {
                                reel: feature[0][1],
                                row: feature[0][0],
                            },
                            targetCoord: targetArr.map((coord: ApocalypseCoordType) => {
                                return {
                                    reel: coord[1],
                                    row: coord[0],
                                    symbolSize: coord[2],
                                };
                            }),
                        };
                    }
                );
            }

            if (m5Features.length > 0) {
                this.m5RevealMap = data.featuresMapFSM5.map(
                    (feature: ApocalypseM5ServerType): ApocalypseM5Obj => {
                        return {
                            m5Coord: {
                                reel: feature[0][1],
                                row: feature[0][0],
                            },
                            targetCoord: {
                                reel: feature[1][0][0][0][1],
                                row: feature[1][0][0][0][0],
                                symbolName: feature[1][0][1],
                            },
                        };
                    }
                );
            }
        }

        this.resetPersistanceMap(data);

        const winningPositions: boolean[][] = JSON.parse(JSON.stringify(falseArray));

        const getWinningPosition = (): boolean[][] => {
            if (data.mode !== AbstractGameMode.NORMAL) {
                if (data.firstStage) {
                    data.reels.forEach((reel, reelIndex) => {
                        winningPositions[reelIndex] = [];
                        reel.forEach((symbol, symbolIndex) => {
                            winningPositions[reelIndex][symbolIndex] =
                                (data.evaluatedArea[reelIndex][
                                    symbolIndex
                                ] as ApocalypseSymbolNames) !== (symbol as ApocalypseSymbolNames);
                        });
                    });
                } else if (data.secondStage) {
                    data.winMatrixFS.forEach((winReel, winReelIndex) => {
                        winReel.forEach((winSymbol, winSymbolIndex) => {
                            if (
                                (winSymbolIndex === 0 ||
                                    winSymbolIndex === reelsConfig.reelsNumber - 1) &&
                                winSymbol >= 1
                            ) {
                                if (
                                    (data.evaluatedArea[winReelIndex][winSymbolIndex] ===
                                        ApocalypseSymbolNames.M1 ||
                                        data.evaluatedArea[winReelIndex][winSymbolIndex] ===
                                            ApocalypseSymbolNames.M2) &&
                                    data.thisSpinWinning > 0
                                ) {
                                    winningPositions[winReelIndex][winSymbolIndex] = true;
                                } else if (
                                    data.evaluatedArea[winReelIndex][winSymbolIndex] ===
                                        ApocalypseSymbolNames.M4 &&
                                    data.thisSpinWinning > 0
                                ) {
                                    this.featuresMap.map((feature) => {
                                        if (
                                            feature.symbolCoord.reel === winSymbolIndex &&
                                            feature.symbolCoord.row === winReelIndex
                                        ) {
                                            feature.targetCoord.forEach((coord) => {
                                                winningPositions[coord.reel][coord.row] = true;
                                            });
                                        }
                                    });
                                }
                            } else {
                                if (
                                    winSymbol > 0 &&
                                    data.reels[winReelIndex][winSymbolIndex] !==
                                        data.evaluatedArea[winReelIndex][winSymbolIndex]
                                ) {
                                    winningPositions[winReelIndex][winSymbolIndex] = true;
                                } else if (
                                    winSymbol > 0 &&
                                    data.symbolSizesBeforeUpdates[winReelIndex][winSymbolIndex] <
                                        data.symbolSizes[winReelIndex][winSymbolIndex]
                                ) {
                                    winningPositions[winReelIndex][winSymbolIndex] = true;
                                }
                            }
                        });
                    });
                }
            } else {
                winningPositions.forEach((reel) => {
                    reel.splice(-2);
                });

                const nudgePositionOnReel: number[] = [-1, -1, -1, -1, -1, -1];

                data.betWayWins.forEach((betWayWin) => {
                    data.reels.forEach((reel, reelIndex) => {
                        if (reelIndex <= betWayWin.length - 1) {
                            reel.forEach((symbol, symbolIndex) => {
                                if (symbolIndex > 0 && symbolIndex < 5) {
                                    if (
                                        (symbol === betWayWin.symbol ||
                                            symbol === ApocalypseSpecialSymbolNames.WILD ||
                                            symbol === ApocalypseSpecialSymbolNames.XNUDGE) &&
                                        !winningPositions[reelIndex][symbolIndex - 1]
                                    ) {
                                        winningPositions[reelIndex][symbolIndex - 1] = true;

                                        if (
                                            this.hasWildsToNudge &&
                                            (reelIndex === 2 || reelIndex === 3) &&
                                            symbol !== ApocalypseSpecialSymbolNames.WILD
                                        ) {
                                            winningPositions[reelIndex][symbolIndex - 1] = false;
                                        } else if (
                                            this.hasWildsToNudge &&
                                            reelIndex === 2 &&
                                            symbol === ApocalypseSpecialSymbolNames.WILD
                                        ) {
                                            winningPositions[reelIndex][symbolIndex - 1] = false;
                                        }

                                        if (symbol === ApocalypseSpecialSymbolNames.XNUDGE) {
                                            nudgePositionOnReel[reelIndex] = symbolIndex - 1;
                                        } else {
                                            if (
                                                nudgePositionOnReel[reelIndex] !== -1 &&
                                                symbolIndex > nudgePositionOnReel[reelIndex]
                                            ) {
                                                winningPositions[reelIndex][symbolIndex - 1] =
                                                    false;
                                            }
                                        }
                                    }
                                }
                            });
                        }
                    });
                });
            }

            return winningPositions;
        };

        this.finalWinningPosition = getWinningPosition();

        this.currentSymbols = clonedReels;

        if (data.mode === AbstractGameMode.NORMAL && data.nextMode === AbstractGameMode.NORMAL) {
            this.secondStageTeaserRounds = [];
            this.currentSecondStageRound = 0;
        } else {
            this.setTeaserRounds(data);
        }

        if (data.thisSpinWinning === 0) {
            this.lostRounds++;
        }

        super.onGame(data);
    }

    public getBetPrice(): number {
        const hasFreeBets: boolean = Apocalypse.apiPlugin.freeBets.hasFreeBets();
        const bet: string = Apocalypse.apiPlugin.freeBets.getBet();
        const level: string = Apocalypse.apiPlugin.betLevel.getLevel();

        const betLevel: string = hasFreeBets ? bet : level;
        let price: number = Apocalypse.model.gameData?.playedBetValue || parseFloat(betLevel);

        if (this.data.wasFeatureBuy || this.data.enhancedBet) {
            for (let index = 0; index < this.initData.featureBuyTimesBetValue.length; index++) {
                if (this.initData.featureBuyTimesBetValue[index].name === this.buyFeatureName) {
                    price *= this.initData.featureBuyTimesBetValue[index].price;
                    break;
                }
            }
        }

        return price;
    }

    private getAccumulatedRoundWin(): number {
        const acWinInit = this.initData.accumulatedRoundWin;
        const acWinGame = this.gameData ? this.gameData.accumulatedRoundWin : 0;

        return this.isRestore ? acWinInit : acWinGame;
    }

    private getSpinWin(): number {
        return this.isRestore ? this.initData.totalSpinWinnings : this.gameData.totalSpinWinnings;
    }

    public isWinBelowStake(isAccumulated: boolean = true): boolean {
        const price = this.getBetPrice();
        const accumulatedRoundWin = isAccumulated
            ? this.getAccumulatedRoundWin()
            : this.getSpinWin();

        return (
            Apocalypse.apiPlugin.gameClientConfiguration.belowStakeWinRestriction &&
            accumulatedRoundWin <= price
        );
    }
}
